/**
 * FAQ - Interactivité et animations
 * 
 * Ce script gère l'accordéon pour les éléments FAQ
 * - Prend en charge la navigation au clavier
 * - Animation fluide à l'ouverture/fermeture
 * - Support complet d'accessibilité avec ARIA
 * - Optimisé pour les performances
 */

(function() {
  'use strict';
  
  class FaqAccordion {
    constructor() {
      this.openClass = 'open';
      this.animationDuration = 400;
      this.init();
    }
    
    init() {
      // Supprimer tous les anciens événements
      this.removeAllEventListeners();
      
      // Ajouter les événements pour les questions FAQ
      document.querySelectorAll('[data-faq-question]').forEach(question => {
        question.addEventListener('click', this.handleFaqClick.bind(this));
        question.addEventListener('keydown', this.handleKeydown.bind(this));
      });
      
      // Ajouter les événements pour les sous-catégories
      document.querySelectorAll('[data-faq-subsection-toggle]').forEach(toggle => {
        toggle.addEventListener('click', this.handleSubsectionClick.bind(this));
        toggle.addEventListener('keydown', this.handleKeydown.bind(this));
      });
      
      // Gestion du redimensionnement
      this.handleResize();
    }
    
    removeAllEventListeners() {
      // Cloner et remplacer les éléments pour supprimer tous les événements
      document.querySelectorAll('[data-faq-question], [data-faq-subsection-toggle]').forEach(element => {
        const newElement = element.cloneNode(true);
        element.parentNode.replaceChild(newElement, element);
      });
    }
    
    handleFaqClick(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const button = event.currentTarget;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      
      if (button.hasAttribute('data-animating')) return;
      
      if (isExpanded) {
        this.closeFaq(button);
      } else {
        this.openFaq(button);
      }
    }
    
    handleSubsectionClick(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const button = event.currentTarget;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      
      if (button.hasAttribute('data-animating')) return;
      
      if (isExpanded) {
        this.closeSubsection(button);
      } else {
        this.openSubsection(button);
      }
    }
    
    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (event.currentTarget.hasAttribute('data-faq-question')) {
          this.handleFaqClick(event);
        } else if (event.currentTarget.hasAttribute('data-faq-subsection-toggle')) {
          this.handleSubsectionClick(event);
        }
      }
    }
    
    openFaq(button) {
      const content = document.getElementById(button.getAttribute('aria-controls'));
      if (!content) return;
      
      button.setAttribute('data-animating', 'true');
      button.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      
      content.style.height = '0px';
      content.style.display = 'block';
      
      // Force reflow
      void content.offsetHeight;
      
      const height = this.measureContentHeight(content);
      content.style.height = `${height}px`;
      content.classList.add(this.openClass);
      
      setTimeout(() => {
        content.style.height = 'auto';
        button.removeAttribute('data-animating');
      }, this.animationDuration);
    }
    
    closeFaq(button) {
      const content = document.getElementById(button.getAttribute('aria-controls'));
      if (!content) return;
      
      button.setAttribute('data-animating', 'true');
      button.setAttribute('aria-expanded', 'false');
      content.setAttribute('aria-hidden', 'true');
      
      const height = content.scrollHeight;
      content.style.height = `${height}px`;
      
      // Force reflow
      void content.offsetHeight;
      
      content.style.height = '0px';
      content.classList.remove(this.openClass);
      
      setTimeout(() => {
        content.style.display = 'none';
        content.style.height = '';
        button.removeAttribute('data-animating');
      }, this.animationDuration);
    }
    
    openSubsection(button) {
      const content = document.getElementById(button.getAttribute('aria-controls'));
      if (!content) return;
      
      // Marquer comme en cours d'animation
      button.setAttribute('data-animating', 'true');
      
      // Mise à jour des attributs ARIA
      button.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');
      
      // Préparation pour l'animation
      content.style.display = 'block';
      content.style.height = '0px';
      content.style.opacity = '0';
      
      // Force reflow pour garantir que les styles sont appliqués
      void content.offsetHeight;
      
      // Mesurer la hauteur cible
      const targetHeight = this.measureContentHeight(content);
      
      // Démarrer l'animation
      content.style.height = `${targetHeight}px`;
      content.style.opacity = '1';
      content.classList.add(this.openClass);
      
      // Nettoyage après l'animation
      setTimeout(() => {
        if (button.getAttribute('aria-expanded') === 'true') {
          content.style.height = 'auto';
        }
        button.removeAttribute('data-animating');
      }, this.animationDuration + 50);
    }
    
    closeSubsection(button) {
      const content = document.getElementById(button.getAttribute('aria-controls'));
      if (!content) return;
      
      button.setAttribute('data-animating', 'true');
      button.setAttribute('aria-expanded', 'false');
      content.setAttribute('aria-hidden', 'true');
      
      const currentHeight = content.scrollHeight;
      content.style.height = `${currentHeight}px`;
      
      // Force reflow
      void content.offsetHeight;
      
      content.style.height = '0px';
      content.style.opacity = '0';
      content.classList.remove(this.openClass);
      
      setTimeout(() => {
        if (button.getAttribute('aria-expanded') === 'false') {
          content.style.display = 'none';
          content.style.height = '';
          content.style.opacity = '';
        }
        button.removeAttribute('data-animating');
      }, this.animationDuration + 50);
    }
    
    measureContentHeight(content) {
      // Créer un clone pour mesurer la hauteur sans affecter l'original
      const clone = content.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.visibility = 'hidden';
      clone.style.height = 'auto';
      clone.style.maxHeight = 'none';
      clone.style.overflow = 'visible';
      clone.classList.add(this.openClass);
      
      content.parentNode.appendChild(clone);
      const height = clone.scrollHeight;
      content.parentNode.removeChild(clone);
      
      return height;
    }
    
    handleResize() {
      let timeout;
      window.addEventListener('resize', () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          // Recalcule la hauteur des éléments FAQ ouverts
          document.querySelectorAll('[data-faq-question][aria-expanded="true"]').forEach(button => {
            const content = document.getElementById(button.getAttribute('aria-controls'));
            if (content && content.classList.contains(this.openClass)) {
              content.style.height = 'auto';
            }
          });
          
          // Recalcule la hauteur des sous-catégories ouvertes
          document.querySelectorAll('[data-faq-subsection-toggle][aria-expanded="true"]').forEach(button => {
            const content = document.getElementById(button.getAttribute('aria-controls'));
            if (content && content.classList.contains(this.openClass)) {
              content.style.height = 'auto';
            }
          });
        }, 100);
      });
    }
  }
  
  // Initialisation quand le DOM est prêt
  function initFaq() {
    if (document.querySelector('[data-faq-question], [data-faq-subsection-toggle]')) {
      new FaqAccordion();
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFaq);
  } else {
    initFaq();
  }
  
  // Réinitialiser après les changements du theme editor
  document.addEventListener('shopify:section:load', initFaq);
  document.addEventListener('shopify:section:reorder', initFaq);
  
})(); 